/**
 * Floor keypad table lookup — pure exact-name matching over the enabled
 * branch tables ("Mesa no encontrada" toast when null).
 * Ported verbatim from cartadigital-pos app/utils/floor/keypad.ts.
 */

export interface IKeypadTableCandidate {
  id: number;
  name: string;
}

function normalizeName(value: string): string {
  return value.trim().toLocaleLowerCase('es');
}

/**
 * Exact (trimmed, case-insensitive) name match. When the query is purely
 * numeric, table names that equal the number ("12") or a "<word> 12"
 * suffix ("Mesa 12") also match — keypads only type digits.
 */
export function findTableByKeypadQuery<T extends IKeypadTableCandidate>(
  tables: readonly T[],
  query: string
): T | null {
  const q = normalizeName(query);
  if (!q) return null;

  const exact = tables.find((t) => normalizeName(t.name) === q);
  if (exact) return exact;

  if (!/^\d+$/.test(q)) return null;
  // Strip leading zeros so "05" opens "Mesa 5".
  const num = String(Number(q));

  const numericMatches = tables.filter((t) => {
    const name = normalizeName(t.name);
    if (name === num) return true;
    // "<anything> 12" — last whitespace-separated token equals the number.
    const lastToken = name.split(/\s+/).pop() ?? '';
    return /^\d+$/.test(lastToken) && String(Number(lastToken)) === num;
  });

  return numericMatches.length === 1 ? numericMatches[0] ?? null : null;
}
